"use client";

import { useState, useEffect, useRef, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import VideoPlayer from "../social/posts/VideoPlayer";

// Upload Progress Bar Component
const UploadProgressBar = memo(({ progressControllerRef, fileCount, t, userTypeColor, compact = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (progressControllerRef) {
      progressControllerRef.current = {
        show: () => setIsVisible(true),
        hide: () => {
          setIsVisible(false);
          setProgress(0);
        },
        setProgress: (value) => setProgress(value),
        reset: () => {
          setProgress(0);
          setIsVisible(false);
        },
      };
    }
  }, [progressControllerRef]);

  if (!isVisible) return null;

  return (
    <div
      style={{
        marginBottom: compact ? "0" : "12px",
        padding: compact ? "8px 12px" : "10px 14px",
        background: "rgba(248, 249, 250, 0.8)",
        border: "none",
        borderRadius: "6px",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {/* Progress Bar */}
        <div
          style={{
            flex: "1 1 auto",
            height: "4px",
            background: "rgba(233, 236, 239, 0.6)",
            borderRadius: "2px",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: userTypeColor,
              borderRadius: "2px",
              transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)",
                animation: "shimmer 1.5s infinite",
              }}
            />
          </div>
        </div>

        {/* Status Text and Percentage */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "500",
              color: "#6b7280",
              whiteSpace: "nowrap",
            }}
          >
            {fileCount > 0
              ? `${fileCount} ${fileCount === 1 ? "file" : "files"}`
              : ""}
          </span>
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: userTypeColor,
              minWidth: "36px",
              textAlign: "right",
            }}
          >
            {Math.round(progress)}%
          </span>
        </div>
      </div>
    </div>
  );
});

UploadProgressBar.displayName = "UploadProgressBar";

// Media Display Component
const MediaDisplay = memo(({ media, onRemove }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset index when media changes
  useEffect(() => {
    if (currentIndex >= media.length && media.length > 0) {
      setCurrentIndex(media.length - 1);
    } else if (media.length === 0) {
      setCurrentIndex(0);
    }
  }, [media.length, currentIndex]);

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const closeModal = useCallback(() => {
    setSelectedImageIndex(null);
  }, []);

  // Handle keyboard events (Escape to close)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Stop propagation to prevent parent modals from closing
        e.stopPropagation();
        closeModal();
      }
    };

    if (selectedImageIndex !== null) {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [selectedImageIndex, closeModal]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  if (media.length === 0) return null;

  const currentItem = media[currentIndex];

  return (
    <>
      <div
        style={{
          position: "relative",
          width: "100%",
          border: "1px solid #ddd",
          borderRadius: "8px",
          overflow: "hidden",
          background: "#f9f9f9",
          minHeight: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {currentItem.type === "video" ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              minHeight: "400px",
              background: "#000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <VideoPlayer
              src={currentItem.url}
              thumbnail={currentItem.thumbnail}
              autoPlay={false}
            />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={() => handleImageClick(currentIndex)}
          >
            <img
              src={currentItem.url}
              alt={`Media ${currentIndex + 1}`}
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "contain",
                display: "block",
                cursor: "pointer",
              }}
              draggable={false}
            />
          </div>
        )}

        {/* Remove Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(currentItem.id || currentIndex);
            // Adjust index if removing current item
            if (currentIndex >= media.length - 1 && currentIndex > 0) {
              setCurrentIndex(currentIndex - 1);
            }
          }}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "rgba(0, 0, 0, 0.7)",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            cursor: "pointer",
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            transition: "background 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "rgba(0, 0, 0, 0.9)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "rgba(0, 0, 0, 0.7)";
          }}
        >
          ×
        </button>

        {/* Navigation Arrows */}
        {media.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToPrevious();
              }}
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(0, 0, 0, 0.6)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(0, 0, 0, 0.8)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgba(0, 0, 0, 0.6)";
              }}
            >
              ‹
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                goToNext();
              }}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(0, 0, 0, 0.6)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                transition: "background 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "rgba(0, 0, 0, 0.8)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "rgba(0, 0, 0, 0.6)";
              }}
            >
              ›
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {media.length > 1 && (
          <div
            style={{
              position: "absolute",
              bottom: "15px",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: "8px",
              zIndex: 10,
            }}
          >
            {media.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  goToSlide(index);
                }}
                style={{
                  width: currentIndex === index ? "24px" : "8px",
                  height: "8px",
                  borderRadius: "4px",
                  border: "none",
                  background: currentIndex === index ? "white" : "rgba(255, 255, 255, 0.5)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  padding: 0,
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Slide Counter */}
        {media.length > 1 && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              background: "rgba(0, 0, 0, 0.7)",
              color: "white",
              padding: "4px 12px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: "500",
              zIndex: 10,
            }}
          >
            {currentIndex + 1} / {media.length}
          </div>
        )}
      </div>

      {selectedImageIndex !== null && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            cursor: "pointer",
          }}
          onClick={closeModal}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={media[selectedImageIndex].url}
              alt={`Media ${selectedImageIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />

            <button
              onClick={closeModal}
              style={{
                position: "absolute",
                top: "-40px",
                right: "0px",
                background: "rgba(0, 0, 0, 0.7)",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>

            {media.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex((prev) =>
                      prev === 0 ? media.length - 1 : prev - 1
                    );
                  }}
                  style={{
                    position: "absolute",
                    left: "-50px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    cursor: "pointer",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex((prev) =>
                      prev === media.length - 1 ? 0 : prev + 1
                    );
                  }}
                  style={{
                    position: "absolute",
                    right: "-50px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    border: "none",
                    borderRadius: "50%",
                    width: "40px",
                    height: "40px",
                    cursor: "pointer",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
});

MediaDisplay.displayName = "MediaDisplay";

const PostModal = ({
  isOpen,
  onClose,
  mode = "create",
  editingPost = null,
  currentUser,
  onCreatePost,
  onUpdatePost,
  initialPrivacy = "everyone",
  pendingFileType = null, // "image" or "video"
  hidePrivacyDropdown = false,
}) => {
  const { getUserType, getProfileImageUrl } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const userType = getUserType();
  const userTypeColor = getUserTypeColor(userType);

  const [postContent, setPostContent] = useState("");
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedDocumentFiles, setSelectedDocumentFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacy, setPrivacy] = useState("everyone");
  const [removedMediaIds, setRemovedMediaIds] = useState([]);
  const [error, setError] = useState(null);
  const [showPrivacyDropdown, setShowPrivacyDropdown] = useState(false);
  const [isRepost, setIsRepost] = useState(false);
  const [pollOptions, setPollOptions] = useState([]);
  const [pollDuration, setPollDuration] = useState(7);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    alignLeft: false,
    alignCenter: false,
    alignRight: false,
    alignJustify: false,
  });

  const uploadProgressRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const contentEditableRef = useRef(null);
  const privacyDropdownRef = useRef(null);

  // Refs for unsaved changes check
  const postContentRef = useRef(postContent);
  const selectedMediaRef = useRef(selectedMedia);

  useEffect(() => {
    postContentRef.current = postContent;
    selectedMediaRef.current = selectedMedia;
  }, [postContent, selectedMedia]);

  const handleAddPollOption = () => {
    setPollOptions((prev) => [...prev, ""]);
  };

  const handleRemovePollOption = (index) => {
    if (pollOptions.length > 2) {
      setPollOptions((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Function to detect if text contains Arabic characters
  const isArabicText = (text) => {
    if (!text || text.trim() === "") return false;
    // Strip HTML tags for detection
    const textContent = text.replace(/<[^>]*>/g, "").trim();
    if (textContent === "") return false;
    const firstChar = textContent.charAt(0);
    const arabicRegex =
      /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return arabicRegex.test(firstChar);
  };

  // Formatting functions
  const handleFormatCommand = (command, value = null) => {
    if (!contentEditableRef.current) return;

    contentEditableRef.current.focus();
    document.execCommand(command, false, value);
    updateActiveFormats();
  };

  // Alignment handler
  const handleAlignment = (alignment) => {
    if (!contentEditableRef.current) return;

    contentEditableRef.current.focus();
    const command =
      alignment === "left" ? "justifyLeft" :
        alignment === "center" ? "justifyCenter" :
          alignment === "right" ? "justifyRight" :
            "justifyFull";

    document.execCommand(command, false, null);
    updateActiveFormats();
  };

  const updateActiveFormats = () => {
    if (!contentEditableRef.current) return;

    // Get the current selection
    const selection = window.getSelection();
    let alignLeft = false;
    let alignCenter = false;
    let alignRight = false;
    let alignJustify = false;

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let element = range.commonAncestorContainer;

      // Get the block element (div, p, etc.)
      if (element.nodeType !== Node.ELEMENT_NODE) {
        element = element.parentElement;
      }

      // Traverse up to find the block element
      while (element && element !== contentEditableRef.current) {
        const computedStyle = window.getComputedStyle(element);
        const textAlign = computedStyle.textAlign;

        if (textAlign === "left" || textAlign === "start") {
          alignLeft = true;
          break;
        } else if (textAlign === "center") {
          alignCenter = true;
          break;
        } else if (textAlign === "right" || textAlign === "end") {
          alignRight = true;
          break;
        } else if (textAlign === "justify") {
          alignJustify = true;
          break;
        }

        element = element.parentElement;
      }
    }

    const formats = {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikethrough: document.queryCommandState("strikeThrough"),
      alignLeft,
      alignCenter,
      alignRight,
      alignJustify,
    };
    setActiveFormats(formats);
  };

  const handleContentChange = () => {
    if (!contentEditableRef.current) return;
    const htmlContent = contentEditableRef.current.innerHTML;
    // Clean up empty contentEditable artifacts
    const cleanedContent = htmlContent === "<br>" || htmlContent === "<div><br></div>" || htmlContent.trim() === "" ? "" : htmlContent;
    setPostContent(cleanedContent);
    // Normalize empty content for placeholder display
    if (cleanedContent === "" && (htmlContent === "<br>" || htmlContent === "<div><br></div>")) {
      contentEditableRef.current.innerHTML = "";
    }
    adjustEditorHeight();
  };

  const adjustEditorHeight = () => {
    if (!contentEditableRef.current) return;
    const editor = contentEditableRef.current;
    const minHeight = 120;
    const maxHeight = 400;

    editor.style.height = "auto";
    const newHeight = Math.max(minHeight, Math.min(editor.scrollHeight, maxHeight));
    editor.style.height = `${newHeight}px`;
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  // Handle keyboard shortcuts
  const handleEditorKeyDown = (e) => {
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

    if (ctrlKey && e.key === "b") {
      e.preventDefault();
      handleFormatCommand("bold");
    } else if (ctrlKey && e.key === "i") {
      e.preventDefault();
      handleFormatCommand("italic");
    } else if (ctrlKey && e.key === "u") {
      e.preventDefault();
      handleFormatCommand("underline");
    } else if (ctrlKey && e.shiftKey && e.key === "X") {
      e.preventDefault();
      handleFormatCommand("strikeThrough");
    }
  };

  // Close privacy dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        privacyDropdownRef.current &&
        !privacyDropdownRef.current.contains(event.target)
      ) {
        setShowPrivacyDropdown(false);
      }
    };

    if (showPrivacyDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showPrivacyDropdown]);

  // Load editing post data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && editingPost) {
        // Check if this is a repost
        const isRepostValue = editingPost.is_repost ||
          editingPost.repost_id ||
          editingPost.type === 'repost' ||
          (editingPost.reposted_post && Object.keys(editingPost.reposted_post).length > 0) ||
          (editingPost.original_post && Object.keys(editingPost.original_post).length > 0);

        setIsRepost(isRepostValue);

        // For reposts, only show repost comment text, not original post content
        // For regular posts, show content
        const content = isRepostValue
          ? (editingPost.repost_response_text || editingPost.repost_comment || editingPost.content || "")
          : (editingPost.content || "");
        setPostContent(content);
        setPrivacy(editingPost.privacy || "everyone");

        // For reposts: DO NOT show any media (original post media should not be editable)
        // For regular posts: load existing media normally
        if (isRepost) {
          // Reposts don't have their own media - set empty array
          setSelectedMedia([]);
        } else {
          // Regular posts: load existing media
          if (editingPost.media && Array.isArray(editingPost.media)) {
            const existingMedia = editingPost.media.map((item, index) => ({
              id: item.id || `existing_${index}`,
              url: item.url || item.path || item,
              type: item.type || (
                (item.url?.match(/\.(mp4|mov|webm|m3u8)(\?|$)/i) || item.url?.includes("video"))
                  ? "video"
                  : "image"
              ),
              isExisting: true,
              originalId: item.id,
              thumbnail: item.thumbnail || item.preview_url || null,
            }));
            setSelectedMedia(existingMedia);
          } else {
            setSelectedMedia([]);
          }
        }

        setSelectedFiles([]);
        setSelectedDocuments([]);
        setSelectedDocumentFiles([]);
        setRemovedMediaIds([]);
        setError(null);
        setIsSubmitting(false);

        // Populate poll options if present
        const existingPollOptions = Array.isArray(editingPost.pollOptions)
          ? editingPost.pollOptions
          : Array.isArray(editingPost.poll_options)
            ? editingPost.poll_options
            : [];
        setPollOptions(existingPollOptions.map((opt) => opt?.text || opt?.option || opt?.label || opt || ""));
        setPollDuration(editingPost.poll_duration || 7);

        // Set contentEditable content and focus after a short delay
        setTimeout(() => {
          if (contentEditableRef.current) {
            contentEditableRef.current.innerHTML = content;
            adjustEditorHeight();
            contentEditableRef.current.focus({ preventScroll: true });
            updateActiveFormats();
          }
        }, 100);
      } else {
        // Reset for create mode
        setPostContent("");
        setSelectedMedia([]);
        setSelectedFiles([]);
        setSelectedDocuments([]);
        setSelectedDocumentFiles([]);
        setPrivacy(initialPrivacy);
        setRemovedMediaIds([]);
        setIsRepost(false);
        setPollOptions([]);
        setPollDuration(7);
        setError(null);
        setIsSubmitting(false);

        // Clear contentEditable
        setTimeout(() => {
          if (contentEditableRef.current) {
            contentEditableRef.current.innerHTML = "";
            adjustEditorHeight();
            contentEditableRef.current.focus({ preventScroll: true });
          }
        }, 100);
      }
    }
  }, [isOpen, mode, editingPost, initialPrivacy]);

  // Handle close with confirmation
  const handleClose = useCallback(() => {
    if (mode === "create" && !isSubmitting) {
      // Check for content (stripping HTML tags)
      const content = postContentRef.current || "";
      const hasContent = content.replace(/<[^>]*>/g, "").trim().length > 0;
      const hasMedia = selectedMediaRef.current && selectedMediaRef.current.length > 0;

      if (hasContent || hasMedia) {
        const message = t("common.discardChanges") || "Discard unsaved changes?";
        if (window.confirm(message)) {
          onClose();
        }
        return;
      }
    }
    onClose();
  }, [mode, isSubmitting, onClose, t]);

  // Handle keyboard events
  useEffect(() => {
    if (isOpen) {
      const handleKeyDown = (e) => {
        if (e.key === "Escape" && !isSubmitting) {
          handleClose();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, isSubmitting, handleClose]);

  // Handle body scroll locking
  useEffect(() => {
    if (isOpen) {
      // Save current scroll
      const savedScrollY = window.scrollY;

      // Lock scroll
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${savedScrollY}px`;

      return () => {
        // Unlock scroll
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';

        // Restore scroll position instantly
        window.scrollTo({
          top: savedScrollY,
          behavior: 'instant'
        });
      };
    }
  }, [isOpen]);

  // Auto-resize editor on content change
  useEffect(() => {
    if (contentEditableRef.current && isOpen) {
      adjustEditorHeight();
    }
  }, [postContent, isOpen]);

  // Open file picker automatically when modal opens with pendingFileType
  useEffect(() => {
    if (isOpen && pendingFileType && mode === "create") {
      // Small delay to ensure modal is fully rendered and animation completes
      const timer = setTimeout(() => {
        if (pendingFileType === "image" && imageInputRef.current) {
          imageInputRef.current.click();
        } else if (pendingFileType === "video" && videoInputRef.current) {
          videoInputRef.current.click();
        }
      }, 350); // Wait for modal animation to complete (300ms animation + 50ms buffer)

      return () => clearTimeout(timer);
    }
  }, [isOpen, pendingFileType, mode]);

  const handleImageUpload = useCallback(
    (event) => {
      const files = Array.from(event.target.files);

      if (files.length > 0) {
        const validFiles = files.filter((file) => {
          const isValidType = file.type.startsWith("image/");
          if (!isValidType) {
            setError(
              t("postCreation.invalidFileTypeAlert", { fileName: file.name })
            );
            return false;
          }

          const maxSize = 1000 * 1024 * 1024;
          if (file.size > maxSize) {
            setError(
              t("postCreation.fileTooLargeAlert", { fileName: file.name })
            );
            return false;
          }

          return true;
        });

        if (validFiles.length === 0) return;

        const maxFiles = 10;
        const currentCount = selectedFiles.length;
        const remainingSlots = maxFiles - currentCount;

        if (remainingSlots <= 0) {
          setError(t("postCreation.maxFilesAlert"));
          return;
        }

        const filesToProcess = validFiles.slice(0, remainingSlots);
        setSelectedFiles((prev) => [...prev, ...filesToProcess]);

        filesToProcess.forEach((file, index) => {
          const previewUrl = URL.createObjectURL(file);
          setSelectedMedia((prev) => {
            const newMedia = {
              id: Date.now() + Math.random() + index,
              url: previewUrl,
              type: "image",
              file: file,
              name: file.name,
              size: file.size,
              isNew: true,
            };
            return [...prev, newMedia];
          });
        });

        if (event.target) {
          event.target.value = "";
        }
      }
    },
    [selectedFiles.length, t]
  );

  // Generate a thumbnail from a local video File by seeking to ~1s and capturing a canvas frame
  const generateVideoThumbnail = useCallback((file) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.muted = true;
      video.preload = 'metadata';
      video.src = url;
      video.onloadedmetadata = () => {
        // Seek to 1s or halfway through, whichever is smaller
        video.currentTime = Math.min(1, video.duration / 2);
      };
      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          URL.revokeObjectURL(url);
          resolve(dataUrl);
        } catch {
          URL.revokeObjectURL(url);
          resolve(null);
        }
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
    });
  }, []);

  const handleVideoUpload = useCallback(
    (event) => {
      const files = Array.from(event.target.files);

      if (files.length > 0) {
        const validFiles = files.filter((file) => {
          const isValidType = file.type.startsWith("video/");
          if (!isValidType) {
            setError(
              t("postCreation.invalidFileTypeAlert", { fileName: file.name })
            );
            return false;
          }

          const maxSize = 1000 * 1024 * 1024;
          if (file.size > maxSize) {
            setError(
              t("postCreation.fileTooLargeAlert", { fileName: file.name })
            );
            return false;
          }

          return true;
        });

        if (validFiles.length === 0) return;

        const maxFiles = 10;
        const currentCount = selectedFiles.length;
        const remainingSlots = maxFiles - currentCount;

        if (remainingSlots <= 0) {
          setError(t("postCreation.maxFilesAlert"));
          return;
        }

        const filesToProcess = validFiles.slice(0, remainingSlots);
        setSelectedFiles((prev) => [...prev, ...filesToProcess]);

        filesToProcess.forEach(async (file, index) => {
          // Use createObjectURL for efficient video preview
          const objectUrl = URL.createObjectURL(file);

          // Add media immediately so UI shows the player right away
          const mediaId = Date.now() + Math.random() + index;
          setSelectedMedia((prev) => {
            const newMedia = {
              id: mediaId,
              url: objectUrl,
              type: "video",
              file: file,
              name: file.name,
              size: file.size,
              isNew: true,
              thumbnail: null,
            };
            return [...prev, newMedia];
          });

          // Generate thumbnail asynchronously and update once ready
          const thumbnail = await generateVideoThumbnail(file);
          if (thumbnail) {
            setSelectedMedia((prev) =>
              prev.map((m) => m.id === mediaId ? { ...m, thumbnail } : m)
            );
          }
        });

        if (event.target) {
          event.target.value = "";
        }
      }
    },
    [selectedFiles.length, t, generateVideoThumbnail]
  );

  const removeMedia = useCallback((mediaId) => {
    setSelectedMedia((prev) => {
      const mediaItem = prev.find((m) => (m.id || m) === mediaId);
      if (mediaItem && mediaItem.isExisting && mediaItem.originalId) {
        setRemovedMediaIds((prevIds) => [...prevIds, mediaItem.originalId]);
      }
      return prev.filter((m) => (m.id || m) !== mediaId);
    });
    setSelectedFiles((prev) => {
      const mediaIndex = selectedMedia.findIndex((m) => (m.id || m) === mediaId);
      if (mediaIndex >= 0 && selectedMedia[mediaIndex]?.isNew) {
        return prev.filter((_, index) => index !== mediaIndex);
      }
      return prev;
    });
  }, [selectedMedia]);

  const handleDocumentUpload = useCallback(
    (event) => {
      const files = Array.from(event.target.files);

      if (files.length > 0) {
        const validFiles = files.filter((file) => {
          const isMedia =
            file.type.startsWith("image/") || file.type.startsWith("video/");
          if (isMedia) {
            setError(
              t("postCreation.invalidDocumentTypeAlert", { fileName: file.name })
            );
            return false;
          }

          const maxSize = 1000 * 1024 * 1024;
          if (file.size > maxSize) {
            setError(
              t("postCreation.documentFileTooLargeAlert", { fileName: file.name })
            );
            return false;
          }

          return true;
        });

        if (validFiles.length === 0) return;

        const maxFiles = 10;
        const currentCount = selectedDocumentFiles.length;
        const remainingSlots = maxFiles - currentCount;

        if (remainingSlots <= 0) {
          setError(t("postCreation.maxDocumentsAlert"));
          return;
        }

        const filesToProcess = validFiles.slice(0, remainingSlots);
        setSelectedDocumentFiles((prev) => [...prev, ...filesToProcess]);

        filesToProcess.forEach((file, index) => {
          const newDocument = {
            id: Date.now() + Math.random() + index,
            name: file.name,
            size: file.size,
            type: file.type,
            file: file,
          };
          setSelectedDocuments((prev) => [...prev, newDocument]);
        });

        if (event.target) {
          event.target.value = "";
        }
      }
    },
    [selectedDocumentFiles.length, t]
  );

  const removeDocument = useCallback((documentId) => {
    setSelectedDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
    setSelectedDocumentFiles((prev) => {
      const docIndex = selectedDocuments.findIndex((doc) => doc.id === documentId);
      return prev.filter((_, index) => index !== docIndex);
    });
  }, [selectedDocuments]);

  const handleSubmit = async () => {
    // Strip HTML tags for validation
    const textContent = postContent.replace(/<[^>]*>/g, "").trim();
    if (
      !textContent &&
      selectedMedia.length === 0
    ) {
      setError(t("postCreation.emptyPostError") || "Please add some content");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Check if editing a repost
      const isRepost = mode === "edit" && editingPost && (
        editingPost.is_repost ||
        editingPost.repost_id ||
        editingPost.type === 'repost' ||
        (editingPost.reposted_post && Object.keys(editingPost.reposted_post).length > 0) ||
        (editingPost.original_post && Object.keys(editingPost.original_post).length > 0)
      );

      let postData;

      if (isRepost) {
        // For reposts: send repost comment fields, NOT content
        // Do NOT send post_type, files, or removedMediaIds
        postData = {
          repost_response_text: postContent,
          repost_comment: postContent, // Alternative field name
          privacy: privacy,
        };
      } else {
        // For regular posts: send content and post_type
        const resolvedPostType = (mode === "edit" && editingPost) 
          ? (editingPost.post_type || editingPost.type || (editingPost.is_question ? "question" : "post")) 
          : "post";

        postData = {
          content: postContent,
          post_type: resolvedPostType,
          privacy: privacy,
        };

        if (mode === "edit" && editingPost?.is_question) {
          postData.is_question = true;
        }

        if (selectedFiles.length > 0) {
          postData.files = selectedFiles;
        }

        // For edit mode, include removed media IDs (only for regular posts)
        if (mode === "edit" && removedMediaIds.length > 0) {
          postData.removedMediaIds = removedMediaIds;
        }
        
        // Include poll options if they exist
        if (pollOptions.length > 0) {
          postData.poll_options = pollOptions.filter(opt => opt.trim() !== "");
          postData.poll_duration = pollDuration;
        }
      }

      const totalFiles = selectedFiles.length;
      const hasFiles = totalFiles > 0;

      if (uploadProgressRef.current) {
        uploadProgressRef.current.show();
        uploadProgressRef.current.setProgress(0);
      }

      if (!hasFiles && uploadProgressRef.current) {
        uploadProgressRef.current.setProgress(100);
      }

      if (mode === "edit" && editingPost) {
        await onUpdatePost(editingPost.id, postData);
      } else {
        // Non-blocking call for new posts
        onCreatePost(postData);
      }

      // Hide progress bar after a short delay (for edit mode mostly)
      setTimeout(() => {
        if (uploadProgressRef.current) {
          uploadProgressRef.current.hide();
        }
      }, 500);

      onClose();
    } catch (error) {
      console.error("Error submitting post:", error);
      setError(error.message || t("postCreation.submitError") || "Failed to submit post");
      if (uploadProgressRef.current) {
        uploadProgressRef.current.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className="post-modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
        animation: "fadeIn 0.2s ease-out",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div
        className="post-modal-content"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "900px",
          width: "100%",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          position: "relative",
          animation: "modalSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          transformOrigin: "center",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            paddingBottom: "16px",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {/* User Avatar */}
            <img
              src={
                getProfileImageUrl() ||
                getUserPlaceholderImage(currentUser)
              }
              alt={currentUser?.name || "Profile"}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                objectFit: "cover",
                border: `2px solid ${userTypeColor}`,
              }}
              onError={(e) => {
                e.target.src = getUserPlaceholderImage(currentUser);
              }}
            />
            {/* User Name */}
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1F2937",
                fontFamily: '"Red Hat Display", sans-serif',
              }}
            >
              {currentUser?.full_name || currentUser?.name || (currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() : "User")}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {/* Privacy Dropdown Button */}
            {!hidePrivacyDropdown && (
              <div
                ref={privacyDropdownRef}
              style={{
                position: "relative",
              }}
            >
              <button
                type="button"
                onClick={() => setShowPrivacyDropdown(!showPrivacyDropdown)}
                disabled={isSubmitting}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  background: "white",
                  border: `1px solid ${userTypeColor}`,
                  borderRadius: "8px",
                  color: userTypeColor,
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  opacity: isSubmitting ? 0.5 : 1,
                  transition: "all 0.2s ease",
                  fontFamily: '"Red Hat Display", sans-serif',
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = "#f9fafb";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = "white";
                  }
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <span>
                  {privacy === "everyone"
                    ? t("privacySettings.options.everyone", "Everyone")
                    : privacy === "friends"
                      ? t("privacySettings.options.friends", "Friends")
                      : privacy === "friends_of_friends"
                        ? t("privacySettings.options.friends_of_friends", "Friends of Friends")
                        : t("privacySettings.options.no_one", "Nobody")}
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: showPrivacyDropdown ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Privacy Dropdown Menu */}
              {showPrivacyDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: "8px",
                    backgroundColor: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                    zIndex: 1000,
                    minWidth: "200px",
                    overflow: "hidden",
                  }}
                >
                  {[
                    {
                      value: "everyone",
                      label: t("privacySettings.options.everyone", "Everyone"),
                      icon: (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      ),
                    },
                    {
                      value: "friends",
                      label: t("privacySettings.options.friends", "Friends"),
                      icon: (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      ),
                    },
                    {
                      value: "friends_of_friends",
                      label: t(
                        "privacySettings.options.friends_of_friends",
                        "Friends of Friends"
                      ),
                      icon: (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          <path d="M22 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      ),
                    },
                    {
                      value: "no_one",
                      label: t("privacySettings.options.no_one", "Nobody"),
                      icon: (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      ),
                    },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setPrivacy(option.value);
                        setShowPrivacyDropdown(false);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "100%",
                        padding: "10px 14px",
                        background: privacy === option.value ? userTypeColor : "transparent",
                        color: privacy === option.value ? "white" : "#4b5563",
                        border: "none",
                        fontSize: "14px",
                        fontWeight: privacy === option.value ? "600" : "500",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        fontFamily: '"Red Hat Display", sans-serif',
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => {
                        if (privacy !== option.value) {
                          e.currentTarget.style.background = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (privacy !== option.value) {
                          e.currentTarget.style.background = "transparent";
                        }
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center" }}>
                        {option.icon}
                      </span>
                      <span style={{ flex: 1 }}>{option.label}</span>
                      {privacy === option.value && (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                color: "#6B7280",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                padding: "0",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                transition: "all 0.2s ease",
                opacity: isSubmitting ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.target.style.color = "#1F2937";
                  e.target.style.backgroundColor = "#F3F4F6";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) {
                  e.target.style.color = "#6B7280";
                  e.target.style.backgroundColor = "transparent";
                }
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          marginBottom: "16px"
        }}>
          {/* Editor */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0",
            }}
          >
            {/* Formatting Toolbar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "4px 6px",
                background: "#f9f9f9",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                borderBottomLeftRadius: "0",
                borderBottomRightRadius: "0",
              }}
            >
              <button
                type="button"
                onClick={() => handleFormatCommand("bold")}
                title="Bold (Ctrl+B / Cmd+B)"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background: activeFormats.bold ? userTypeColor : "transparent",
                  color: activeFormats.bold ? "white" : "#4b5563",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  fontSize: "16px",
                  fontWeight: "bold",
                }}
                onMouseEnter={(e) => {
                  if (!activeFormats.bold) {
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeFormats.bold) {
                    e.target.style.background = "transparent";
                  }
                }}
              >
                B
              </button>
              <button
                type="button"
                onClick={() => handleFormatCommand("italic")}
                title="Italic (Ctrl+I / Cmd+I)"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background: activeFormats.italic ? userTypeColor : "transparent",
                  color: activeFormats.italic ? "white" : "#4b5563",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  fontSize: "16px",
                  fontStyle: "italic",
                }}
                onMouseEnter={(e) => {
                  if (!activeFormats.italic) {
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeFormats.italic) {
                    e.target.style.background = "transparent";
                  }
                }}
              >
                I
              </button>
              <button
                type="button"
                onClick={() => handleFormatCommand("underline")}
                title="Underline (Ctrl+U / Cmd+U)"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background: activeFormats.underline ? userTypeColor : "transparent",
                  color: activeFormats.underline ? "white" : "#4b5563",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  fontSize: "16px",
                  textDecoration: "underline",
                }}
                onMouseEnter={(e) => {
                  if (!activeFormats.underline) {
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeFormats.underline) {
                    e.target.style.background = "transparent";
                  }
                }}
              >
                U
              </button>
              <button
                type="button"
                onClick={() => handleFormatCommand("strikeThrough")}
                title="Strikethrough (Ctrl+Shift+X / Cmd+Shift+X)"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background: activeFormats.strikethrough ? userTypeColor : "transparent",
                  color: activeFormats.strikethrough ? "white" : "#4b5563",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  fontSize: "16px",
                  textDecoration: "line-through",
                }}
                onMouseEnter={(e) => {
                  if (!activeFormats.strikethrough) {
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeFormats.strikethrough) {
                    e.target.style.background = "transparent";
                  }
                }}
              >
                S
              </button>

              {/* Divider */}
              <div
                style={{
                  width: "1px",
                  height: "20px",
                  background: "#e5e7eb",
                  margin: "0 2px",
                }}
              />

              {/* Alignment Buttons */}
              <button
                type="button"
                onClick={() => handleAlignment("left")}
                title={t("postCreation.alignLeft") || "Align Left"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background: activeFormats.alignLeft ? userTypeColor : "transparent",
                  color: activeFormats.alignLeft ? "white" : "#4b5563",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!activeFormats.alignLeft) {
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeFormats.alignLeft) {
                    e.target.style.background = "transparent";
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="21" y1="10" x2="7" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="21" y1="18" x2="7" y2="18"></line>
                </svg>
              </button>

              <button
                type="button"
                onClick={() => handleAlignment("center")}
                title={t("postCreation.alignCenter") || "Align Center"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background: activeFormats.alignCenter ? userTypeColor : "transparent",
                  color: activeFormats.alignCenter ? "white" : "#4b5563",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!activeFormats.alignCenter) {
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeFormats.alignCenter) {
                    e.target.style.background = "transparent";
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="10" x2="6" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="18" y1="18" x2="6" y2="18"></line>
                </svg>
              </button>

              <button
                type="button"
                onClick={() => handleAlignment("right")}
                title={t("postCreation.alignRight") || "Align Right"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background: activeFormats.alignRight ? userTypeColor : "transparent",
                  color: activeFormats.alignRight ? "white" : "#4b5563",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!activeFormats.alignRight) {
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeFormats.alignRight) {
                    e.target.style.background = "transparent";
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="21" y1="10" x2="7" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="21" y1="18" x2="7" y2="18"></line>
                </svg>
              </button>

              <button
                type="button"
                onClick={() => handleAlignment("justify")}
                title={t("postCreation.alignJustify") || "Justify"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background: activeFormats.alignJustify ? userTypeColor : "transparent",
                  color: activeFormats.alignJustify ? "white" : "#4b5563",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!activeFormats.alignJustify) {
                    e.target.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!activeFormats.alignJustify) {
                    e.target.style.background = "transparent";
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="21" y1="10" x2="3" y2="10"></line>
                  <line x1="21" y1="6" x2="3" y2="6"></line>
                  <line x1="21" y1="14" x2="3" y2="14"></line>
                  <line x1="21" y1="18" x2="3" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* ContentEditable Editor */}
            <div
              ref={contentEditableRef}
              contentEditable="true"
              onInput={handleContentChange}
              onKeyDown={handleEditorKeyDown}
              onPaste={handlePaste}
              onBlur={updateActiveFormats}
              data-placeholder={t("postCreation.textareaPlaceholder") || "What's on your mind?"}
              style={{
                flex: 1,
                border: "1px solid #ddd",
                borderTop: "none",
                borderRadius: "8px",
                borderTopLeftRadius: "0",
                borderTopRightRadius: "0",
                padding: "12px 16px",
                fontSize: "16px",
                outline: "none",
                background: "#f9f9f9",
                fontFamily: '"Red Hat Display", sans-serif',
                transition: "border-color 0.3s ease, height 0.2s ease",
                resize: "vertical",
                minHeight: "120px",
                maxHeight: "400px",
                overflowY: "auto",
                direction:
                  isArabicText(postContent) || i18n.language?.startsWith("ar")
                    ? "rtl"
                    : "ltr",
                textAlign:
                  isArabicText(postContent) || i18n.language?.startsWith("ar")
                    ? "right"
                    : "left",
              }}
            />
          </div>

          {/* Poll Options UI */}
          {pollOptions.length > 0 && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                background: "#f9f9f9",
              }}
            >
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "12px",
                  color: "#374151",
                }}
              >
                {t("channelPost.types.poll") || "Poll Options"}
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {pollOptions.map((opt, index) => (
                  <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => {
                        const newOptions = [...pollOptions];
                        newOptions[index] = e.target.value;
                        setPollOptions(newOptions);
                      }}
                      placeholder={t(
                        "channelPostCreation.poll.optionPlaceholder",
                        "Option {{index}}",
                        { index: index + 1 }
                      )}
                      disabled={isSubmitting}
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                        background: "white",
                        color: "#1f2937",
                        fontFamily: '"Red Hat Display", sans-serif',
                        outline: "none",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = userTypeColor)}
                      onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                    />
                    {pollOptions.length > 2 && (
                      <button
                        type="button"
                        onClick={() => handleRemovePollOption(index)}
                        disabled={isSubmitting}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                          padding: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "4px",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#fee2e2")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" }}>
                <button
                  type="button"
                  onClick={handleAddPollOption}
                  disabled={isSubmitting}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: userTypeColor,
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  {t("channelPostCreation.poll.addOption", "+ Add option")}
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <label style={{ fontSize: "14px", color: "#4b5563" }}>
                    {t("channelPostCreation.poll.durationLabel", "Duration (days):")}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={pollDuration}
                    onChange={(e) => setPollDuration(parseInt(e.target.value) || 7)}
                    disabled={isSubmitting}
                    style={{
                      width: "60px",
                      padding: "6px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = userTypeColor)}
                    onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Media Preview */}
          {selectedMedia.length > 0 && (
            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                background: "#f9f9f9",
              }}
            >
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "12px",
                  color: "#374151",
                }}
              >
                {t("postFeed.media") || "Media"}
              </h4>
              <MediaDisplay media={selectedMedia} onRemove={removeMedia} />
            </div>
          )}

          {/* Hidden File Inputs */}
          <input
            ref={imageInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: "none" }}
            disabled={isSubmitting}
          />
          <input
            ref={videoInputRef}
            type="file"
            multiple
            accept="video/*"
            onChange={handleVideoUpload}
            style={{ display: "none" }}
            disabled={isSubmitting}
          />

          {/* Error Message */}
          {error && (
            <div
              style={{
                padding: "12px",
                background: "#FEE2E2",
                border: "1px solid #FCA5A5",
                borderRadius: "8px",
                color: "#991B1B",
                fontSize: "14px",
                marginBottom: "16px",
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="post-modal-footer"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            paddingTop: "16px",
            borderTop: "1px solid #e5e7eb",
            flexWrap: "wrap",
          }}
        >
          {/* Left: Photo and Video Buttons - Hide for reposts and polls */}
          {!(mode === "edit" && isRepost) && pollOptions.length === 0 && (
            <div
              className="post-modal-footer-media-buttons"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flex: "0 0 auto",
              }}
            >
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                disabled={isSubmitting || selectedMedia.length >= 10}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 14px",
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor:
                    isSubmitting || selectedMedia.length >= 10
                      ? "not-allowed"
                      : "pointer",
                  opacity: isSubmitting || selectedMedia.length >= 10 ? 0.5 : 1,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && selectedMedia.length < 10) {
                    e.currentTarget.style.background = "#f9fafb";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting && selectedMedia.length < 10) {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.borderColor = "#e5e7eb";
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span style={{ color: "#3b82f6" }}>{t("postCreation.addImage") || "Photo"}</span>
              </button>

              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                disabled={isSubmitting || selectedMedia.length >= 10}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 14px",
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor:
                    isSubmitting || selectedMedia.length >= 10
                      ? "not-allowed"
                      : "pointer",
                  opacity: isSubmitting || selectedMedia.length >= 10 ? 0.5 : 1,
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting && selectedMedia.length < 10) {
                    e.currentTarget.style.background = "#f9fafb";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting && selectedMedia.length < 10) {
                    e.currentTarget.style.background = "white";
                    e.currentTarget.style.borderColor = "#e5e7eb";
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
                <span style={{ color: "#10b981" }}>{t("postCreation.addVideo") || "Video"}</span>
              </button>

              {selectedMedia.length > 0 && (
                <span style={{ fontSize: "12px", color: "#6b7280", marginLeft: "4px" }}>
                  {selectedMedia.length}/10
                </span>
              )}
            </div>
          )}

          {/* Center: Upload Progress Bar */}
          <div
            className="post-modal-footer-progress"
            style={{
              flex: "1 1 auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minWidth: "200px",
              maxWidth: "400px",
              margin: "0 12px",
            }}
          >
            <div style={{ width: "100%", maxWidth: "100%" }}>
              <UploadProgressBar
                progressControllerRef={uploadProgressRef}
                fileCount={selectedFiles.length}
                t={t}
                userTypeColor={userTypeColor}
                compact={true}
              />
            </div>
          </div>

          {/* Right: Cancel and Post/Update Buttons */}
          <div
            className="post-modal-footer-action-buttons"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: "0 0 auto",
            }}
          >
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                padding: "10px 24px",
                background: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                color: "#374151",
                fontSize: "14px",
                fontWeight: "500",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                opacity: isSubmitting ? 0.5 : 1,
                transition: "all 0.2s ease",
              }}
            >
              {t("common.cancel") || "Cancel"}
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                (!postContent.replace(/<[^>]*>/g, "").trim() &&
                  selectedMedia.length === 0)
              }
              style={{
                padding: "10px 24px",
                background:
                  isSubmitting ||
                    (!postContent.replace(/<[^>]*>/g, "").trim() &&
                      selectedMedia.length === 0)
                    ? "#ccc"
                    : userTypeColor,
                border: "none",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor:
                  isSubmitting ||
                    (!postContent.replace(/<[^>]*>/g, "").trim() &&
                      selectedMedia.length === 0)
                    ? "not-allowed"
                    : "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {isSubmitting ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid #ffffff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  {mode === "edit"
                    ? t("postCreation.updating") || "Updating..."
                    : t("postCreation.posting") || "Posting..."}
                </div>
              ) : mode === "edit" ? (
                t("postCreation.update") || "Update"
              ) : (
                t("postCreation.post") || "Post"
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .post-modal-overlay {
          animation: fadeIn 0.2s ease-out;
        }
        .post-modal-content {
          animation: modalSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        /* Mobile Full Screen Modal */
        @media (max-width: 768px) {
          .post-modal-overlay {
            padding: 0 !important;
            align-items: flex-start !important;
            background-color: rgba(0, 0, 0, 0.8) !important;
          }
          
          .post-modal-content {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 100vh !important;
            height: 100dvh !important;
            max-height: 100vh !important;
            max-height: 100dvh !important;
            border-radius: 0 !important;
            padding: 12px !important;
            padding-bottom: calc(12px + env(safe-area-inset-bottom)) !important;
            margin: 0 !important;
            box-shadow: none !important;
            animation: modalSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
          }
          
          .post-modal-content > div:first-child {
            margin-bottom: 12px !important;
            padding-bottom: 12px !important;
          }
          
          .post-modal-content > div:last-child {
            padding-top: 12px !important;
          }
          
          @keyframes modalSlideUp {
            from {
              opacity: 0;
              transform: translateY(100%);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        }
        [contenteditable="true"][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable="true"]:focus {
          border-color: ${userTypeColor} !important;
        }
        
        /* Mobile Responsive Footer */
        @media (max-width: 768px) {
          .post-modal-footer {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          
          .post-modal-footer-media-buttons {
            width: 100%;
            justify-content: flex-start;
            flex: 1 1 100%;
            order: 1;
          }
          
          .post-modal-footer-media-buttons button {
            flex: 0 1 auto;
            min-width: 0;
          }
          
          .post-modal-footer-progress {
            width: 100%;
            flex: 1 1 100%;
            margin: 0;
            min-width: 0;
            max-width: 100%;
            order: 2;
            display: flex;
            justify-content: center;
          }
          
          .post-modal-footer-progress > div {
            width: 100%;
            max-width: 100%;
          }
          
          .post-modal-footer-action-buttons {
            width: 100%;
            justify-content: stretch;
            flex: 1 1 100%;
            order: 3;
            gap: 8px;
          }
          
          .post-modal-footer-action-buttons button {
            flex: 1 1 0;
            min-width: 0;
          }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PostModal;

